import { useEffect, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material'
import { ClientSummary } from '../../api/types'
import { useOpsApi } from '../../api/context'
import { SectionCard } from '../../components/SectionCard'
import { SegmentedTabs } from '../../components/SegmentedTabs'
import { normalizePersonalNumber } from '../../validation'
import { ClientFormState, ClientMode } from './formState'

interface ClientSectionProps {
  /** Imperium zobrazuje navíc pole pro své dokumenty (doklad, narození, koresp. adresa). */
  showImperiumFields: boolean
  mode: ClientMode
  onModeChange: (mode: ClientMode) => void
  selectedClient: ClientSummary | null
  onSelectExisting: (client: ClientSummary | null) => void
  form: ClientFormState
  onFormChange: (patch: Partial<ClientFormState>) => void
  errors: Record<string, string>
}

function optionLabel(client: ClientSummary): string {
  const id = client.personalNumber ?? client.companyId
  return id ? `${client.fullName} (${id})` : client.fullName
}

/**
 * Výběr/založení klienta: přepínač NOVÝ (plný formulář) / STÁVAJÍCÍ (autocomplete
 * lookup dle jména/RČ/IČO). U stávajícího klienta jsou údaje jen pro čtení —
 * mění se na kartě klienta v hlavní aplikaci (rozhodnutí F2-E). U nového klienta
 * dedup hint podle RČ/IČO s nabídkou „použít stávajícího".
 */
export function ClientSection({
  showImperiumFields,
  mode,
  onModeChange,
  selectedClient,
  onSelectExisting,
  form,
  onFormChange,
  errors,
}: ClientSectionProps) {
  const api = useOpsApi()

  // Stávající klient = read-only formulář; úpravy se dělají na kartě klienta (F2-E).
  const readOnly = mode === 'EXISTING' && Boolean(selectedClient)

  const [searchInput, setSearchInput] = useState('')
  const [options, setOptions] = useState<ClientSummary[]>([])
  const [searching, setSearching] = useState(false)
  const [duplicates, setDuplicates] = useState<ClientSummary[]>([])

  // Debounced hledání pro autocomplete stávajícího klienta.
  useEffect(() => {
    if (mode !== 'EXISTING') return
    // Prázdný/krátký dotaz vrací z BE VŠECHNY klienty teritoria — nehledáme.
    if (searchInput.trim().length < 2) {
      setOptions([])
      setSearching(false)
      return
    }
    let cancelled = false
    setSearching(true)
    const timer = setTimeout(() => {
      api
        .searchClients(searchInput)
        .then((found) => {
          if (!cancelled) setOptions(found)
        })
        .catch(() => {
          if (!cancelled) setOptions([])
        })
        .finally(() => {
          if (!cancelled) setSearching(false)
        })
    }, 300)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [api, mode, searchInput])

  // Dedup hint: po opuštění pole RČ/IČO v režimu NOVÝ zkusit lookup.
  const handleDedupLookup = () => {
    if (mode !== 'NEW') return
    const personalNumber = form.company ? undefined : normalizePersonalNumber(form.personalNumber)
    const companyId = form.company ? form.companyId.trim() : undefined
    if (!personalNumber && !companyId) {
      setDuplicates([])
      return
    }
    api
      .lookupClient({ personalNumber, companyId })
      .then(setDuplicates)
      .catch(() => setDuplicates([]))
  }

  const handleModeChange = (value: string) => {
    setDuplicates([])
    setSearchInput('')
    onModeChange(value as ClientMode)
  }

  const field = (
    name: keyof ClientFormState,
    label: string,
    extra: Record<string, unknown> = {}
  ) => (
    <TextField
      size="small"
      sx={{ flexGrow: 1, minWidth: 180 }}
      label={label}
      value={form[name]}
      onChange={(event) => onFormChange({ [name]: event.target.value })}
      error={Boolean(errors[name])}
      helperText={errors[name]}
      disabled={readOnly}
      {...extra}
    />
  )

  const row = (children: React.ReactNode) => (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>{children}</Box>
  )

  return (
    <SectionCard title="Klient">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <SegmentedTabs
          items={[
            { value: 'NEW', label: 'Nový klient' },
            { value: 'EXISTING', label: 'Stávající klient' },
          ]}
          value={mode}
          onChange={handleModeChange}
        />

        {mode === 'EXISTING' && (
          <Autocomplete
            size="small"
            options={options}
            value={selectedClient}
            onChange={(_event, value) => onSelectExisting(value)}
            inputValue={searchInput}
            onInputChange={(_event, value) => setSearchInput(value)}
            getOptionLabel={optionLabel}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={searching}
            noOptionsText="Žádný klient nenalezen"
            loadingText="Hledám…"
            // Filtrování dělá mock/BE (jméno i RČ/IČO) — FE nefiltruje podruhé.
            filterOptions={(x) => x}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Vyhledat klienta (jméno, RČ, IČO)"
                error={Boolean(errors.selectedClient)}
                helperText={errors.selectedClient}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {searching && <CircularProgress size={16} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        )}

        {mode === 'EXISTING' && selectedClient && (
          <Typography variant="body2" color="text.secondary">
            Údaje klienta jsou jen pro čtení — změny se dělají na kartě klienta v hlavní aplikaci.
          </Typography>
        )}

        {(mode === 'NEW' || selectedClient) && (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.company}
                  onChange={(event) => onFormChange({ company: event.target.checked })}
                  disabled={readOnly}
                />
              }
              label="Právnická osoba"
            />

            {form.company &&
              row(
                <>
                  {field('companyName', 'Název společnosti *')}
                  {field('companyId', 'IČO *', { onBlur: handleDedupLookup })}
                </>
              )}

            {row(
              <>
                {field('titlePre', 'Titul před', { sx: { width: 110, flexGrow: 0 } })}
                {field('firstName', 'Jméno')}
                {field('surname', form.company ? 'Příjmení (jednající osoba)' : 'Příjmení *')}
                {field('titleAfter', 'Titul za', { sx: { width: 110, flexGrow: 0 } })}
              </>
            )}

            {!form.company &&
              row(
                <>
                  {field('personalNumber', 'Rodné číslo *', {
                    placeholder: 'RRMMDD/XXXX',
                    inputProps: { maxLength: 11 },
                    onBlur: handleDedupLookup,
                  })}
                  {showImperiumFields &&
                    field('birthDate', 'Datum narození', {
                      type: 'date',
                      InputLabelProps: { shrink: true },
                    })}
                  {showImperiumFields && field('birthPlace', 'Místo narození')}
                </>
              )}

            {row(
              <>
                {field('street', 'Ulice a č.p. *')}
                {field('town', 'Město *')}
                {field('zipCode', 'PSČ *', { sx: { width: 140, flexGrow: 0 } })}
              </>
            )}

            {showImperiumFields &&
              row(
                <>
                  {field('contactStreet', 'Korespondenční — ulice a č.p.')}
                  {field('contactTown', 'Město')}
                  {field('contactZip', 'PSČ', { sx: { width: 140, flexGrow: 0 } })}
                </>
              )}

            {showImperiumFields &&
              row(
                <>
                  {field('identityCardNumber', 'Číslo dokladu')}
                  {field('identityCardValidity', 'Platnost do', {
                    type: 'date',
                    InputLabelProps: { shrink: true },
                  })}
                  {field('identityCardIssuer', 'Doklad vystavil (úřad)')}
                </>
              )}

            {row(
              <>
                {field('email', 'E-mail *', { type: 'email' })}
                {field('phone', 'Telefon')}
              </>
            )}

          </>
        )}

        {mode === 'NEW' &&
          duplicates.map((duplicate) => (
            <Alert
              key={duplicate.id}
              severity="warning"
              action={
                <Button
                  size="small"
                  color="inherit"
                  onClick={() => {
                    setDuplicates([])
                    onModeChange('EXISTING')
                    onSelectExisting(duplicate)
                  }}
                >
                  Použít stávajícího
                </Button>
              }
            >
              Klient s tímto {form.company ? 'IČO' : 'RČ'} už existuje: {duplicate.fullName}
            </Alert>
          ))}
      </Box>
    </SectionCard>
  )
}
